import { motion, AnimatePresence } from "framer-motion";
import { Star, Linkedin, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

export function TestimonialsPro({
  items = [],
  title = "Hear from Our Clients",
}) {
  const safeItems = items.length ? items : realTestimonials;

  const cardVariants = {
    hidden: { opacity: 0, y: 32, scale: 0.98 },
    show: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: i * 0.06,
        type: "spring",
        stiffness: 120,
        damping: 14,
      },
    }),
  };

  return (
    <section
      id="testimonials"
      className="relative py-24 overflow-hidden bg-gradient-to-b from-white via-white to-gray-50"
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-indigo-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-purple-200/40 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="relative text-center mb-14">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-indigo-50 text-indigo-700"
          >
            <Sparkles size={14} /> Client Love
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-4 text-4xl md:text-5xl font-extrabold leading-tight bg-gradient-to-r from-indigo-600 via-fuchsia-600 to-purple-600 bg-clip-text text-transparent"
          >
            {title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-gray-600 max-w-2xl mx-auto"
          >
            Real outcomes from candidates who trusted us with profile strategy,
            storytelling, and ATS alignment.
          </motion.p>
        </div>

        {/* Grid */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {safeItems.map((t, i) => (
              <motion.article
                key={t.name + i}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
                whileHover={{ y: -6, rotateX: 0.3, rotateY: -0.3 }}
                className="relative rounded-2xl border border-gray-200 bg-white/90 backdrop-blur shadow-lg hover:shadow-2xl transition will-change-transform p-6"
              >
                <div
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(99,102,241,0.08)" }}
                />

                <div className="flex items-center gap-4">
                  <img
                    src={t.img}
                    alt={t.name}
                    className="h-14 w-14 rounded-full object-cover border-2 border-white shadow"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900 leading-tight">
                      {t.name}
                    </h3>
                    <div className="flex items-center gap-1 mt-1">
                      {Array.from({ length: t.rating ?? 5 }).map((_, idx) => (
                        <Star
                          key={idx}
                          size={16}
                          className="text-yellow-400"
                          fill="currentColor"
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-gray-600 leading-relaxed">
                  “{t.quote}”
                </p>

                {t.linkedin && (
                  <a
                    href={t.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    <Linkedin size={18} /> Connect on LinkedIn
                  </a>
                )}
              </motion.article>
            ))}
          </AnimatePresence>
        </div>

        <div className="text-center mt-14">
          <Link
            to="/clients"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition shadow-md"
          >
            Browse all clients <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------- REAL LINKEDIN TESTIMONIAL DATA ---------------- */

const realTestimonials = [
  {
    name: "Shubham Singh",
    quote:
      "Their profile improvements helped me present my storage & infrastructure experience clearly and professionally.",
    img: "https://media.licdn.com/dms/image/v2/D5603AQEpkjWoox3jvg/profile-displayphoto-shrink_400_400/B56ZQ5u42nHQAg-/0/1736135382574?e=1767225600&v=beta&t=RNab1YrlrE4TrQdI0kMwDzhZRIUsHNo5GFSI2PQ6oes",
    rating: 5,
    linkedin: "https://www.linkedin.com/in/shubham-singh-49629a230/",
  },
  // {
  //   name: "Shweta Benke",
  //   quote:
  //     "Clear, structured, and impactful profile content. It significantly improved how recruiters engage with my profile.",
  //   img: "https://media.licdn.com/dms/image/v2/D5635AQHaiMNieNt29Q/profile-framedphoto-shrink_400_400/B56ZaYll0QGkAc-/0/1746316713640?e=1766250000&v=beta&t=ms-Y7sa8Jhy40AEjUc7qyespMUzr8MEDKdTQOfIG2dY",
  //   rating: 5,
  //   linkedin: "https://www.linkedin.com/in/shweta-benke-266a7a203/",
  // },
  {
    name: "Shipra Jaiswal",
    quote:
      "They helped refine my professional story and highlight my strengths. Strongly recommend their services!",
    img: "https://media.licdn.com/dms/image/v2/D5603AQEwgfKAODOsSA/profile-displayphoto-scale_400_400/B56Zm5gcOLJ8Ag-/0/1759753915899?e=1767225600&v=beta&t=U-2HCgikb8PvkzDbkp1MWdm0BupPoYtmsZUu8HxTfI0",
    rating: 5,
    linkedin: "https://www.linkedin.com/in/shipra-jaiswal-883286259/",
  },
  {
    name: "Mohammad Javed",
    quote:
      "Amazing improvement in clarity and structure. The optimized profile truly reflects my technical capabilities.",
    img: "https://media.licdn.com/dms/image/v2/D4D03AQGK0S2feom4sw/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1696508980444?e=1767225600&v=beta&t=_ESL7KJeuhkJyZ_nusEsl4OC1nyFWg6aLYKUEpCf5k4",
    rating: 5,
    linkedin: "https://www.linkedin.com/in/mohammad-javed-517a991a0/",
  },
];
